import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileArrowUp, Trash, DownloadSimple, FilePdf } from "@phosphor-icons/react";
import { formatDate, timeAgo } from "@/lib/format";

const CATEGORIES = [
  "Professional Investor Declaration",
  "Elective Professional Opt-up Form",
  "FCA Classification Form",
  "Supporting Document",
];

export default function UserComplianceDocsDialog({ open, onOpenChange, user }) {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    if (!user) return;
    const r = await api.get(`/admin/users/${user.id}/compliance-docs`);
    setDocs(r.data.documents || []);
  };
  useEffect(() => { if (open) load(); }, [open, user?.id]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Please choose a file"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("category", category);
      if (notes) form.append("notes", notes);
      await api.post(`/admin/users/${user.id}/compliance-docs/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setNotes("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const del = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"?`)) return;
    try {
      await api.delete(`/admin/compliance-docs/${doc.id}`);
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const download = (doc) => {
    const token = localStorage.getItem("ip_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/compliance-docs/${doc.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.filename;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Compliance Documents — {user?.name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Upload and manage FCA compliance paperwork for {user?.email}. Stored securely against this investor profile.
        </p>

        {/* Upload panel */}
        <div className="rounded-xl border border-border p-4 bg-secondary/30">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Category</div>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="compdoc-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Notes (optional)</div>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Signed 01 Aug 2026" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" className="text-xs flex-1" data-testid="compdoc-file-input" />
            <Button onClick={upload} disabled={busy} data-testid="compdoc-upload-btn" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
              <FileArrowUp size={15} className="mr-1.5" /> {busy ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </div>

        {/* Document list */}
        <div className="mt-4">
          {docs.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground italic border border-dashed rounded-lg">
              No documents uploaded yet.
            </div>
          ) : (
            <div className="space-y-2" data-testid="compdoc-list">
              {docs.map((d) => (
                <div key={d.id} className="flex items-center gap-3 rounded-lg border border-border p-3">
                  <div className="h-9 w-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 grid place-items-center shrink-0">
                    <FilePdf size={18} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{d.filename}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      <span className="uppercase tracking-wider text-[10px] font-bold">{d.category}</span>
                      <span className="opacity-50">·</span>
                      <span>Uploaded {timeAgo(d.uploaded_at)}</span>
                      <span className="opacity-50">·</span>
                      <span>by {d.uploaded_by_name || "admin"}</span>
                    </div>
                    {d.notes && <div className="text-xs text-muted-foreground mt-0.5 italic">{d.notes}</div>}
                  </div>
                  <button onClick={() => download(d)} className="h-8 w-8 rounded grid place-items-center hover:bg-secondary text-muted-foreground hover:text-foreground" title="Download">
                    <DownloadSimple size={14} />
                  </button>
                  <button onClick={() => del(d)} className="h-8 w-8 rounded grid place-items-center hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600" title="Delete">
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
